// Central client-side API error recorder.
//
// Every failed call that passes through ApiHandler is appended to a ring
// buffer in localStorage so failures can be inspected after the fact
// (see /ErrorLog for the viewer page). Nothing here is allowed to throw:
// logging must never turn a handled API failure into a crash.

const STORAGE_KEY = "api-error-log";
const MAX_ENTRIES = 300;
const MAX_FIELD_CHARS = 4000;

// Keys that must never be written to disk, at any nesting depth.
const SENSITIVE_KEYS = [
  "token",
  "refreshtoken",
  "expiredtoken",
  "authorization",
  "password",
  "pwd",
  "passwd",
];

function isSensitiveKey(key) {
  const k = String(key).toLowerCase();
  return SENSITIVE_KEYS.some((s) => k === s || k.includes(s));
}

// Deep-clone a value while dropping sensitive keys and cutting off runaway
// structures. Returns plain JSON-safe data.
function sanitize(value, depth) {
  const d = typeof depth === "number" ? depth : 0;
  if (d > 6) return "[max depth]";
  if (value === null || value === undefined) return value;

  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") return value;
  if (t === "function") return "[function]";

  if (Array.isArray(value)) {
    const capped = value.slice(0, 100).map((v) => sanitize(v, d + 1));
    if (value.length > 100) capped.push("[" + (value.length - 100) + " more]");
    return capped;
  }

  if (t === "object") {
    const out = {};
    Object.keys(value).forEach((key) => {
      if (isSensitiveKey(key)) {
        out[key] = "[redacted]";
        return;
      }
      try {
        out[key] = sanitize(value[key], d + 1);
      } catch (e) {
        out[key] = "[unreadable]";
      }
    });
    return out;
  }

  return String(value);
}

function toText(value) {
  if (value === null || value === undefined) return "";
  let text;
  if (typeof value === "string") {
    text = value;
  } else {
    try {
      text = JSON.stringify(sanitize(value), null, 2);
    } catch (e) {
      text = "[unserializable]";
    }
  }
  if (text.length > MAX_FIELD_CHARS) {
    return text.slice(0, MAX_FIELD_CHARS) + "\n… [truncated]";
  }
  return text;
}

// Classify the failure so the viewer can be filtered by cause. The
// distinction that matters most: "network" means no HTTP response ever
// reached the browser (offline, aborted request, timeout, or a response
// the browser blocked for CORS) as opposed to a real status code.
function classify(err) {
  if (!err) return "unknown";
  if (typeof err === "string") return "blocked";
  if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
    return "timeout";
  }
  if (err.__CANCEL__ === true || /cancel/i.test(err.message || "")) {
    return "canceled";
  }
  if (err.response && err.response.status) return "http";
  if (/network/i.test(err.message || "")) return "network";
  return "unknown";
}

/**
 * Turn an axios rejection into a sentence a non-technical user can act on,
 * without hiding the underlying detail. Used both in the submit-failed
 * dialog and as the stored "reason" on a failed order record.
 */
export function describeApiFailure(err) {
  const kind = classify(err);

  if (typeof err === "string") {
    return (
      "Your session was being renewed, so the request was not sent. " +
      "Nothing was saved — please try again."
    );
  }

  if (kind === "canceled") {
    return "The request was cancelled before it finished.";
  }

  if (kind === "timeout") {
    return (
      "The server did not reply in time. The order may or may not have " +
      "reached us — check your order list before submitting again."
    );
  }

  if (kind === "network") {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return (
        "Your device is offline. Your items have been kept — reconnect and " +
        "submit again."
      );
    }
    return (
      "Could not reach the server (no response received). This is usually a " +
      "connection drop or the page reloading mid-request. Your items have " +
      "been kept — please submit again."
    );
  }

  const response = err && err.response ? err.response : null;
  if (response) {
    const status = response.status;
    const body = response.data;
    let serverText = "";
    if (typeof body === "string" && body.trim()) {
      serverText = body.trim().slice(0, 300);
    } else if (body && typeof body === "object") {
      serverText =
        body.message || body.title || body.error || body.Message || "";
      if (!serverText && body.errors) {
        try {
          serverText = JSON.stringify(body.errors).slice(0, 300);
        } catch (e) {
          serverText = "";
        }
      }
    }

    if (status === 401 || status === 403) {
      return (
        "Your login has expired. Please sign in again — your items have been " +
        "kept."
      );
    }
    if (status === 400) {
      return (
        "The server rejected the order details" +
        (serverText ? ": " + serverText : ".") +
        " Please check the quantities and try again."
      );
    }
    if (status === 409) {
      return (
        "This order appears to have been submitted already" +
        (serverText ? ": " + serverText : ".") +
        " Check your order list before submitting again."
      );
    }
    if (status >= 500) {
      return (
        "The server hit an error while saving the order (" +
        status +
        ")" +
        (serverText ? ": " + serverText : ".") +
        " Your items have been kept — please try again."
      );
    }
    return (
      "The server returned an error (" +
      status +
      ")" +
      (serverText ? ": " + serverText : ".")
    );
  }

  return (
    ((err && err.message) || "Unknown error") +
    " — your items have been kept, please try again."
  );
}

function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeRaw(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    return true;
  } catch (e) {
    // Almost certainly a quota error. Shed the oldest half and retry once
    // rather than losing the newest (most relevant) entry.
    try {
      const half = entries.slice(Math.floor(entries.length / 2));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(half));
      return true;
    } catch (e2) {
      return false;
    }
  }
}

function currentUser() {
  try {
    const user = JSON.parse(localStorage.getItem("user-info"));
    if (!user) return { code: "", name: "", appType: "" };
    return {
      code: user.code || "",
      name: user.name || user.userName || "",
      appType: localStorage.getItem("app-type") || "",
    };
  } catch (e) {
    return { code: "", name: "", appType: "" };
  }
}

/**
 * Record one failed API call.
 *
 * @param {object} info
 * @param {string} info.method  HTTP verb (GET/POST/PUT/DELETE)
 * @param {string} info.url     Full request URL
 * @param {any}    info.data    Request payload, if any
 * @param {any}    info.error   The rejection value from axios
 * @param {string} [info.label] Optional tag, e.g. "SubmitOrder"
 */
export function logApiError(info) {
  try {
    const opts = info || {};
    const err = opts.error;
    const response = err && err.response ? err.response : null;
    const user = currentUser();

    const entry = {
      id:
        String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8),
      time: new Date().toISOString(),
      kind: classify(err),
      method: (opts.method || "").toUpperCase(),
      url: opts.url || "",
      label: opts.label || "",
      status: response && response.status ? response.status : "",
      statusText: response && response.statusText ? response.statusText : "",
      message:
        typeof err === "string"
          ? err
          : (err && err.message) || "Unknown error",
      code: (err && err.code) || "",
      requestData: toText(opts.data),
      responseData: response ? toText(response.data) : "",
      page:
        typeof window !== "undefined" && window.location
          ? window.location.pathname
          : "",
      online:
        typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
          ? navigator.onLine
          : null,
      userCode: user.code,
      userName: user.name,
      appType: user.appType,
      userAgent:
        typeof navigator !== "undefined"
          ? String(navigator.userAgent).slice(0, 300)
          : "",
    };

    const entries = readRaw();
    entries.push(entry);
    while (entries.length > MAX_ENTRIES) entries.shift();
    writeRaw(entries);

    // Let any open viewer tab refresh itself immediately.
    try {
      window.dispatchEvent(new CustomEvent("api-error-logged", { detail: entry }));
    } catch (e) {
      /* CustomEvent unsupported — viewer will pick it up on next poll */
    }

    return entry;
  } catch (e) {
    // Logging must never break the caller.
    return null;
  }
}

/** All recorded errors, newest first. */
export function getApiErrors() {
  return readRaw().slice().reverse();
}

/** Wipe the log. */
export function clearApiErrors() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (e) {
    return false;
  }
}

const CSV_COLUMNS = [
  "time",
  "kind",
  "method",
  "url",
  "label",
  "status",
  "statusText",
  "message",
  "code",
  "page",
  "online",
  "userCode",
  "userName",
  "appType",
  "requestData",
  "responseData",
  "userAgent",
];

function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return '"' + s.replace(/"/g, '""') + '"';
}

/** Serialise the log to CSV text. */
export function apiErrorsToCsv() {
  const rows = [CSV_COLUMNS.join(",")];
  getApiErrors().forEach((entry) => {
    rows.push(CSV_COLUMNS.map((c) => csvCell(entry[c])).join(","));
  });
  return rows.join("\r\n");
}

/** Trigger a browser download of the log as CSV. */
export function downloadApiErrorsCsv() {
  try {
    const blob = new Blob(["﻿" + apiErrorsToCsv()], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download =
      "api-error-log-" + new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-") + ".csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    return true;
  } catch (e) {
    console.error("Could not export error log", e);
    return false;
  }
}

const ErrorLogStore = {
  log: logApiError,
  describe: describeApiFailure,
  getAll: getApiErrors,
  clear: clearApiErrors,
  toCsv: apiErrorsToCsv,
  download: downloadApiErrorsCsv,
  STORAGE_KEY,
};

export default ErrorLogStore;
